/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-28 11:39:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm, reset } from 'redux-form';
import { Page, Layout, Card, PageActions, Stack, Button } from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';

import DisplaySettings from './display';
import StyleSettings from './style';

import MainMenu from 'src/components/containers/main-menu';
// import DidYouKnow from 'src/components/views/filter-settings/did-you-know';
import TypeFormEmbedded from 'src/components/views/common/typeform-embedded';
import SendRequestFeatureModal from 'src/components/containers/common/send-request-feature-modal';
import RequestResultModal from 'src/components/containers/common/request-result-modal';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import TitleHelp from 'src/components/views/common/title-help';

import { URL } from 'src/constants/links';
import { routes } from 'src/routes';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import {
  buildInitialFilterSettingsValues,
  buildFilterSettingsValues
} from 'src/helpers/filter-settings-helper';
import { confirmLeavePage } from 'src/helpers';
import { orderObjectProperties } from 'src/helpers';

import * as FilterSettingsActions from 'actions/filter-settings-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import * as FilterDataActions from 'actions/filter-data-actions';
import * as quickStartGuideAction from 'actions/dashboard/quick-start-guide-action';

import type { ComposedProps, State } from './index.type';

class FilterSettings extends Component<ComposedProps, State> {
  state = {
    discardState: 0,

    // Modal send request result
    activeRequestModal: false,
    activeModalResult: false,

    youtubeModal: false,
    selectedTab: null
  };

  componentDidMount() {
    const { getFilterSettings, getFilterData } = this.props;

    getFilterData();
    getFilterSettings();
    const selectedTab = _.get(this.props.location, 'state.selectedTab', 0);
    this.setState({ selectedTab });

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  render() {
    const {
      dirty,
      pristine,
      submitting,
      loadingFilterSettings,
      savingFilterSettings,
      loadingClearCache,
      handleSubmit,
      selectedPlan,
      successSave,
      successClearCache,
      themeSetupV2,
      optionsList,
      appLanguage,
      history,
      i18n
    } = this.props;
    const { discardState, activeRequestModal, activeModalResult, youtubeModal, selectedTab } =
      this.state;
    return (
      <div className="BC-Page BC-Page__Filter-Settings">
        <MainMenu menuId="menu-filters-display-settings" />
        <Page
          title={i18n.translate('AdminHTML.FilterSettings.FilterSettings.pageTitle')}
          primaryAction={
            <div className="BC-Page BC-Page__Button">
              <Stack>
                {!pristine && (
                  <Button disabled={submitting} onClick={this.discardChange}>
                    {i18n.translate('AdminHTML.Common.Action.discard')}
                  </Button>
                )}
                <Button
                  primary
                  onClick={handleSubmit(this.handleSave.bind(this))}
                  disabled={loadingFilterSettings || pristine || submitting}
                  loading={savingFilterSettings && !pristine}>
                  {i18n.translate('AdminHTML.Common.Action.saveSettings')}
                </Button>
              </Stack>
            </div>
          }>
          <TitleHelp
            i18n={i18n}
            onPlay={() => this.toggleModal('youtubeModal')}
            hrefUserManual={URL['Link_V2_Filter_FilterSetting_ManualGuide']}
            onAnalytics={() => history.push(routes.analytics.overviewCollection())}
          />

          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Installation reminder status banner */}
                <InstallationRemindStatusBanner />

                {/* Did you know? */}
                {/* {themeSetupV2 && (
                  <DidYouKnow
                    emailSupport={emailSupport}
                    sendRequest={() => this.toggleModal('activeRequestModal')}
                  />
                )} */}

                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />
              </div>
            </Layout.Section>
            <Layout.Section>
              <form onSubmit={this.handleDefaultSubmit}>
                <div className="displayBox">
                  <DisplaySettings
                    selectedTab={selectedTab}
                    clearCache={this.clearCache}
                    loadingClearCache={loadingClearCache}
                    themeSetupV2={themeSetupV2}
                    optionsList={optionsList}
                    appLanguage={appLanguage}
                  />
                </div>

                <div className="headingStyle">
                  {i18n.translate('AdminHTML.FilterSettings.FilterSettings.headingStyle')}
                </div>

                <div className="styleBox">
                  <Card sectioned>
                    <StyleSettings />
                  </Card>
                </div>
              </form>
            </Layout.Section>
          </Layout>

          <PageActions
            primaryAction={{
              content: i18n.translate('AdminHTML.Common.Action.saveSettings'),
              onAction: handleSubmit(this.handleSave.bind(this)),
              disabled: loadingFilterSettings || pristine || submitting,
              loading: savingFilterSettings && !pristine
            }}
            secondaryActions={
              !pristine
                ? [
                    {
                      content: i18n.translate('AdminHTML.Common.Action.discard'),
                      onAction: this.discardChange
                    }
                  ]
                : []
            }
          />
        </Page>
        {!_.isEmpty(selectedPlan) && (
          <TypeFormEmbedded
            type="filter-settings"
            conditionShow={successSave || successClearCache}
            conditionShowStateChange={discardState}
          />
        )}
        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['Link_V2_Filter_FilterSetting_Video']}
          onClose={() => this.toggleModal('youtubeModal')}
          title={i18n.translate('AdminHTML.FilterSettings.FilterSettings.youtubeModal_Title')}
        />

        <SendRequestFeatureModal
          active={activeRequestModal}
          handleClose={() => this.toggleModal('activeRequestModal')}
          handleOpenResultModal={() => this.toggleModal('activeModalResult')}
          featureKey="filterSettings"
        />
        <RequestResultModal
          active={activeModalResult}
          handleClose={() => this.toggleModal('activeModalResult')}
          featureName={i18n.translate(
            'AdminHTML.Common.RequestResultModal.content_t1_filterSettings'
          )}
        />
        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={dirty}
          // Navigate function
          navigate={path => history.push(path)}
          shouldBlockNavigation={dirty}
        />
      </div>
    );
  }

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  async handleSave(data) {
    const { updateFilterSettings, filterSettings, getChecklist, markDoneStep } = this.props;
    const saveData = buildFilterSettingsValues(data, filterSettings);

    // Sort properties in style
    saveData.style = orderObjectProperties(saveData.style, [
      'filterTitleTextColor',
      'filterTitleFontSize',
      'filterTitleFontWeight',
      'filterTitleFontTransform',
      'filterTitleFontFamily',
      'filterOptionTextColor',
      'filterOptionFontSize',
      'filterOptionFontFamily',
      'filterMobileButtonTextColor',
      'filterMobileButtonFontSize',
      'filterMobileButtonFontWeight',
      'filterMobileButtonFontTransform',
      'filterMobileButtonFontFamily',
      'filterMobileButtonBackgroundColor'
    ]);

    markDoneStep('V2_FILTER2', {
      isDone: true
    });

    await updateFilterSettings(saveData);
    await getChecklist();
  }

  clearCache = async () => {
    const { clearCache, filterSettings, getChecklist } = this.props;
    const { general } = filterSettings;
    const swatchImageVersion = general.swatchImageVersion
      ? parseInt(general.swatchImageVersion) + 1
      : 1;
    const saveData = { ...filterSettings, general: { ...general, swatchImageVersion } };

    await clearCache(saveData);
    await getChecklist();
  };

  discardChange = () => {
    const { resetForm } = this.props;
    const { discardState } = this.state;

    resetForm();
    this.setState({ discardState: discardState + 1 });
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { dirty, i18n } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };
}

const FilterSettingsForm = reduxForm({
  form: 'FilterSettingsForm', // a unique identifier for this form
  enableReinitialize: true
})(FilterSettings);

const mapDispatchToProps = (dispatch, ownProps) => ({
  getFilterData: () => dispatch(FilterDataActions.getFilterData(ownProps.i18n)),
  getFilterSettings: () =>
    dispatch(FilterSettingsActions.getFilterSettings(undefined, ownProps.i18n)),
  updateFilterSettings: (filterSettings: Object) =>
    dispatch(
      FilterSettingsActions.updateFilterSettings(
        filterSettings,
        'filterSettings',
        undefined,
        ownProps.i18n
      )
    ),
  clearCache: (filterSettings: Object) =>
    dispatch(FilterSettingsActions.clearCache(filterSettings, ownProps.i18n)),
  resetForm: () => dispatch(reset('FilterSettingsForm')),
  getChecklist: () => dispatch(checklistActions.getChecklist(ownProps.i18n)),
  markDoneStep: (stepName, value) =>
    dispatch(quickStartGuideAction.markDoneStep(stepName, value, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const { filterSettings } = state;
  const tenantApp = _.get(state.oauth, 'tenantApp', {});

  return {
    filterSettings: filterSettings.filterSettings,
    loadingFilterSettings: filterSettings.loadingFilterSettings,
    savingFilterSettings: filterSettings.savingFilterSettings,
    loadingClearCache: filterSettings.loadingClearCache,
    successSave: filterSettings.successSave,
    successClearCache: filterSettings.successClearCache,
    initialValues: buildInitialFilterSettingsValues(filterSettings.filterSettings, props.i18n),
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan'),
    themeSetupV2: _.get(tenantApp, 'adminAppFeatures.themeSetupV2', false),
    emailSupport: _.get(state.oauth, 'emailSupport'),
    optionsList: _.get(state.filterData, 'filterData.optionsList', []),
    appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE)
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(FilterSettingsForm));
